/**
 * usePageViewBeacon
 *
 * Fires a single fire-and-forget POST to the public visit beacon endpoint.
 * Silent by design — never propagates errors to the caller.
 *
 * Guards:
 * - Only fires when `slug` is truthy and `enabled` is true.
 * - Uses sessionStorage key `beacon:sent:{slug}` to avoid duplicate sends
 *   within the same browser tab/session.
 */

import { useEffect } from 'react';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
// Strip trailing /api to get base URL for public endpoints
const BASE_URL = API_URL.replace(/\/api$/, '');

export function usePageViewBeacon(slug: string | undefined, enabled: boolean): void {
  useEffect(() => {
    if (!slug || !enabled) return;

    const storageKey = `beacon:sent:${slug}`;
    if (sessionStorage.getItem(storageKey)) return;

    const url = `${BASE_URL}/public/tenants/${slug}/visit`;

    // Fetch sem headers custom e sem credentials vira "simple request" — sem preflight,
    // compatível com CORS allowOrigins:['*'] do API Gateway.
    // sendBeacon é evitado porque sempre envia credentials, o que é rejeitado
    // pelo browser quando Access-Control-Allow-Origin é wildcard.
    fetch(url, {
      method: 'POST',
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // Intentionally ignored — beacon is best-effort
    });

    sessionStorage.setItem(storageKey, '1');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, enabled]);
}
